// src/features/locker-assignments/hooks/useLockerAssignmentForm.ts
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useRedirect, useGetIdentity, useGetList, useDataProvider, useNotify, useTranslate } from 'react-admin';

// Versión simplificada del hook sin manejo de tags
export const useLockerAssignmentForm = () => {
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();
  
  // Estado del formulario
  const [selectedHotel, setSelectedHotel] = useState<any>(null);
  const [lockerTypes, setLockerTypes] = useState<any[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const [referenceFields, setReferenceFields] = useState<any[]>([]);
  const [lockerRequirements, setLockerRequirements] = useState<any[]>([]);
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const [referenceValues, setReferenceValues] = useState<Record<string, string>>({});
  const [loadingHotelData, setLoadingHotelData] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // Cargar hoteles
  const { data: hotels, isLoading: loadingHotels } = useGetList('hotels', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: { status: 'active' },
  });

  // Efecto para seleccionar hotel automáticamente si solo hay uno
  useEffect(() => {
    if (hotels && hotels.length > 0 && !selectedHotel) {
      setSelectedHotel(hotels[0]);
    }
  }, [hotels, selectedHotel]);

  // Cargar datos del hotel seleccionado
  useEffect(() => {
    if (!selectedHotel) {
      setLockerTypes([]);
      setZones([]);
      setReferenceFields([]);
      setLockerRequirements([]);
      setSelectedZones([]);
      setReferenceValues({});
      return;
    }

    const loadHotelData = async () => {
      setLoadingHotelData(true);
      try {
        // Cargar tipos de taquillas
        const typesResponse = await dataProvider.getList('locker-types', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
          filter: { hotelId: selectedHotel.id },
        });
        setLockerTypes(typesResponse.data);
        
        // Inicializar con requerimientos para todos los tipos
        if (typesResponse.data.length > 0) {
          setLockerRequirements(
            typesResponse.data.map((type: any, index: number) => ({
              lockerTypeId: type.id,
              typeName: type.name,
              quantity: index === 0 ? 1 : 0, // Primera taquilla con cantidad 1 por defecto
            }))
          );
        }

        // Cargar zonas
        const zonesResponse = await dataProvider.getList('zones', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
          filter: { hotelId: selectedHotel.id },
        });
        const activeZones = zonesResponse.data.filter((z: any) => z.isActive);
        setZones(activeZones);
        
        // Seleccionar todas las zonas activas por defecto
        if (activeZones.length > 0) {
          setSelectedZones(activeZones.map((z: any) => z.id));
        }

        // Cargar campos de referencia
        try {
          const fieldsResponse = await (dataProvider as any).getReferenceFields(selectedHotel.id);
          const fields = fieldsResponse.data?.referenceFields || fieldsResponse.data?.fields || [];
          
          if (fields.length > 0) {
            const sortedFields = [...fields].sort((a: any, b: any) => 
              (a.displayOrder || 0) - (b.displayOrder || 0)
            );
            
            setReferenceFields(sortedFields);
            
            const initialValues: Record<string, any> = {};
            sortedFields.forEach((field: any) => {
              const fieldKey = field.key || `field_${Math.random().toString(36).substr(2, 9)}`;
              initialValues[fieldKey] = field.defaultValue || '';
            });
            setReferenceValues(initialValues);
          } else {
            // Campo de referencia por defecto
            setReferenceFields([{
              key: 'reference',
              label: 'Referencia',
              isMandatory: false,
              fieldType: 'text',
              defaultValue: '',
            }]);
            setReferenceValues({ reference: '' });
          }
        } catch (error) {
          console.warn('Error loading reference fields:', error);
          // Campo de referencia por defecto en caso de error
          setReferenceFields([{
            key: 'reference',
            label: 'Referencia',
            isMandatory: false,
            fieldType: 'text',
            defaultValue: '',
          }]);
          setReferenceValues({ reference: '' });
        }

      } catch (error) {
        notify(translate('resources.locker-assignments.create.error_loading_hotel_data'), { type: 'error' });
        console.error('Error loading hotel data:', error);
      } finally {
        setLoadingHotelData(false);
      }
    };

    loadHotelData();
  }, [selectedHotel, dataProvider, notify, translate]);

  // Manejador de cambio de hotel
  const handleHotelChange = useCallback((event: any) => {
    const hotel = hotels?.find((h: any) => h.id === event.target.value) || null;
    setSelectedHotel(hotel);
  }, [hotels]);

  // Manejadores
  const handleLockerRequirementChange = useCallback((index: number, value: number) => {
    setLockerRequirements(prev =>
      prev.map((req, i) =>
        i === index ? { ...req, quantity: value } : req
      )
    );
    
    // Limpiar error de validación si se corrige
    if (validationErrors.lockerRequirements && value > 0) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.lockerRequirements;
        return newErrors;
      });
    }
  }, [validationErrors]);

  const handleReferenceValueChange = useCallback((key: string, value: any) => {
    setReferenceValues(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleZoneChange = useCallback((event: any) => {
    const value = event.target.value;
    setSelectedZones(typeof value === 'string' ? value.split(',') : value);
  }, []);

  const handleCancel = useCallback(() => {
    redirect('/locker-assignments');
  }, [redirect]);

  // Obtener string de referencia
  const getReferenceString = useCallback(() => {
    return referenceFields
      .map(field => referenceValues[field.key])
      .filter(value => value)
      .join(' - ') || 'Nueva Asignación';
  }, [referenceFields, referenceValues]);

  // Validación del formulario
  const validateForm = useCallback((values: any) => {
    const errors: any = {};
    
    if (!selectedHotel) {
      errors.hotel = translate('resources.locker-assignments.create.validation.hotel_required');
    }
    
    // Validar campos de referencia requeridos
    referenceFields.forEach(field => {
      if (field.isMandatory && !referenceValues[field.key]) {
        errors[`reference_${field.key}`] = translate('resources.locker-assignments.create.validation.field_required', { field: field.label });
      }
    });
    
    // Validar que al menos se requiera una taquilla
    const hasAtLeastOneLocker = lockerRequirements.some(req => req.quantity > 0);
    if (!hasAtLeastOneLocker) {
      errors.lockerRequirements = translate('resources.locker-assignments.create.validation.locker_required');
      setValidationErrors({ lockerRequirements: translate('resources.locker-assignments.create.validation.locker_required') });
    } else {
      setValidationErrors({});
    }
    
    return errors;
  }, [selectedHotel, referenceFields, referenceValues, lockerRequirements, translate]);

  // Transformar datos para envío
  const handleSubmit = useCallback((data: any) => {
    // Filtrar campos temporales/internos
    const { _formInitialized, _initialized, _ready, ...cleanData } = data;
    
    // Preparar referencias
    const references = referenceFields
      .map(field => ({
        key: field.key,
        value: referenceValues[field.key] || '',
      }))
      .filter(ref => ref.value !== '');
    
    // Filtrar solo requerimientos con cantidad > 0
    const requestedLockerTypes = lockerRequirements
      .filter(req => req.quantity > 0)
      .map(req => ({
        lockerTypeId: req.lockerTypeId,
        requestedCount: req.quantity,
      }));
    
    return {
      ...cleanData,
      hotelId: selectedHotel?.id,
      requestedLockerTypes,
      AllowedZoneIds: selectedZones,  // Usar AllowedZoneIds con A mayúscula y terminando en Ids
      references,
      notes: cleanData.notes || '',
    };
  }, [selectedHotel, referenceFields, referenceValues, lockerRequirements, selectedZones]);

  // Valores por defecto del formulario
  const defaultValues = useMemo(() => {
    const now = new Date();
    const endDate = new Date();
    endDate.setHours(23, 0, 0, 0);
    
    return {
      startsAt: now,
      expiresAt: endDate,
      notes: '',
    };
  }, []);

  return {
    // Estado
    selectedHotel,
    hotels,
    lockerTypes,
    zones,
    referenceFields,
    lockerRequirements,
    selectedZones,
    referenceValues,
    loadingHotelData,
    loadingHotels,
    validationErrors,
    
    // Handlers
    handleHotelChange,
    handleLockerRequirementChange,
    handleReferenceValueChange,
    handleZoneChange,
    handleCancel,
    setValidationErrors,
    
    // Funciones de utilidad
    getReferenceString,
    validateForm,
    handleSubmit,
    
    // Valores computados
    defaultValues,
  };
};